import React from 'react';
import {
  Card, CardContent, Box, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, IconButton, Switch, Chip, TablePagination
} from '@mui/material';
import {
  Add as AddIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon, Delete as DeleteIcon, Group as GroupIcon, DirectionsCar as DirectionsCarIcon,
  Pets as PetsIcon, Email as EmailIcon, LockReset as LockResetIcon, Cancel as CancelIcon, Restore as RestoreIcon
} from '@mui/icons-material';
import { Tooltip as MuiTooltip } from '@mui/material';
import SearchInput from '../components/SearchInput';

// Componente controlado: el filtrado, orden y paginación se hacen en el backend
// (GET /admin/residentes con limit/offset/q/sort/dir); aquí solo se pinta la página recibida.
const ResidentesTab = ({
  residentes,
  total,
  page,
  rowsPerPage,
  ordenPor,
  ordenDir,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onSortChange,
  onNuevoResidente,
  onAltaMasiva,
  onEdit,
  onManageContactos,
  onManageVehiculos,
  onManageMascotas,
  onReenviarInvitacion,
  onReenviarResetPassword,
  onDarDeBaja,
  onToggleBloqueo,
  onDarDeBajaAccesoPropiedad,
  onReactivarAccesoPropiedad
}) => {
  const residentesPaginados = residentes || [];

  const handleSort = (campo) => {
    if (ordenPor === campo) onSortChange({ por: campo, dir: ordenDir === 'asc' ? 'desc' : 'asc' });
    else onSortChange({ por: campo, dir: 'asc' });
  };

  return (
    <>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" gap={1}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={onAltaMasiva}>Alta masiva</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={onNuevoResidente}>Nuevo Residente</Button>
            </Box>
          </Box>
          <Box display="flex" gap={2} alignItems="center" sx={{ mt: 2 }}>
            <SearchInput
              onSearch={onSearch}
              placeholder="Buscar por nombre, propiedad, teléfono..."
            />
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ maxHeight: '55vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => handleSort('nombre')} sx={{ cursor: 'pointer' }}>Nombre {ordenPor === 'nombre' && (ordenDir === 'asc' ? <ArrowUpwardIcon fontSize="small"/> : <ArrowDownwardIcon fontSize="small"/>)}</TableCell>
              <TableCell onClick={() => handleSort('propiedad')} sx={{ cursor: 'pointer' }}>Propiedad {ordenPor === 'propiedad' && (ordenDir === 'asc' ? <ArrowUpwardIcon fontSize="small"/> : <ArrowDownwardIcon fontSize="small"/>)}</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Bloqueado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {residentesPaginados.map((r) => {
              const sinAccesoApp = r.bloqueado || r.estado_usuario !== 'activo';
              return (
              <TableRow key={r.id} hover sx={sinAccesoApp ? { opacity: 0.7 } : undefined}>
                <TableCell>
                  <span style={sinAccesoApp ? { textDecoration: 'line-through', color: '#757575' } : undefined}>
                    {r.nombre}
                  </span>
                </TableCell>
                <TableCell>
                  <span style={sinAccesoApp ? { textDecoration: 'line-through', color: '#757575' } : undefined}>
                    {r.propiedad}
                  </span>
                </TableCell>
                <TableCell>{r.categoria}</TableCell>
                <TableCell>
                    <Chip
                      label={r.estado_usuario === 'pendiente' ? 'Pendiente' : (r.bloqueado ? 'Sin acceso' : 'Activo')}
                      color={r.estado_usuario === 'pendiente' || r.bloqueado ? 'warning' : 'success'}
                      size="small"
                      variant="outlined"
                    />
                </TableCell>
                <TableCell>
                  <MuiTooltip title="Bloquea o desbloquea el acceso a la app de toda la propiedad. Al desbloquear, los contactos dados de baja con el ojo naranja siguen sin acceso.">
                    <Switch checked={r.bloqueado} onChange={(e) => onToggleBloqueo(r.id, e.target.checked)} color="error" size="small" />
                  </MuiTooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <MuiTooltip title="Editar residente">
                      <IconButton size="small" color="primary" onClick={() => onEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Gestionar contactos de la propiedad">
                      <IconButton size="small" color="secondary" onClick={() => onManageContactos(r)}><GroupIcon fontSize="small" /></IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Gestionar vehículos de la propiedad">
                      <IconButton size="small" onClick={() => onManageVehiculos(r)}><DirectionsCarIcon fontSize="small" /></IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Gestionar mascotas de la propiedad">
                      <IconButton size="small" onClick={() => onManageMascotas(r)}><PetsIcon fontSize="small" /></IconButton>
                    </MuiTooltip>
                    {r.estado_usuario === 'pendiente' && (
                      <MuiTooltip title="Reenviar invitación de registro">
                        <IconButton size="small" color="info" onClick={() => onReenviarInvitacion(r.id)}><EmailIcon fontSize="small" /></IconButton>
                      </MuiTooltip>
                    )}
                    {r.estado_usuario === 'activo' && (
                      <MuiTooltip title="Enviar correo para restablecer contraseña">
                        <IconButton size="small" onClick={() => onReenviarResetPassword(r.id)}><LockResetIcon fontSize="small" /></IconButton>
                      </MuiTooltip>
                    )}
                    <MuiTooltip title="Dar de baja acceso a la app para toda la propiedad">
                      <IconButton
                        size="small"
                        onClick={() => onDarDeBajaAccesoPropiedad(r.id)}
                        sx={{ color: '#ed6c02' }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Reactivar acceso a la app para toda la propiedad">
                      <IconButton
                        size="small"
                        onClick={() => onReactivarAccesoPropiedad(r.id)}
                        sx={{ color: '#2e7d32' }}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                    <MuiTooltip title="Dar de baja residente (inactiva la cuenta principal)">
                      <IconButton size="small" color="error" onClick={() => onDarDeBaja(r.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );})}
            {residentesPaginados.length === 0 && <TableRow><TableCell colSpan={6} align="center">No hay resultados</TableCell></TableRow>}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total || 0}
          page={page}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Filas:"
        />
      </TableContainer>
    </>
  );
};

export default React.memo(ResidentesTab);
